import React from 'react';

import classNames from 'classnames';
import AvailableSequenceList from 'containers/AvailableSequenceList';
import DragIcon from 'icons/ic-drag.svg';
import { useTranslation } from 'react-i18next';
import { SortableHandle } from 'react-sortable-hoc';
import { animated, useSpring } from 'react-spring';
import { v4 as uuidv4 } from 'uuid';

import usePrevious from 'components/hooks/usePrevious';

import RowBar from 'components/application/RowBar';
import SubmittableField from 'components/application/SubmittableField';

import AddButton from 'components/buttons/AddButton';

const DragHandle = SortableHandle(() => (
  <div className='drag-handle drag-handle--padded m-l-3 m-r-3'>
    <DragIcon
      className='drag-icon [&_path]:tw-fill-grey-300'
      height={18}
      width={8}
    />
  </div>
));

export default function ProjectTrade({
  addError,
  containerRef,
  dispatch,
  isAddingSequence,
  isFocused,
  isOrdering,
  newSequenceNameKey,
  newSequenceText,
  projectTradeId,
  tradeName,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'sequences.ProjectTrade',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const modifiers = ['enlarged', 'border-top-curved'];

  if (isOrdering) modifiers.push('border-radius');

  const wasOrdering = usePrevious(isOrdering);

  const springProps = useSpring({
    transform: isOrdering ? `translateY(-150px)` : 'none',
    immediate: !(wasOrdering && !isOrdering),
  });

  const handleSequenceSubmit = (_event, sequenceText) => {
    axios
      .post(`/project_trades/${projectTradeId}/method_sequences`, {
        method_sequence: {
          name: sequenceText,
        },
      })
      .then((response) => {
        dispatch({ type: 'CLEAR_TOGGLE_CONTENT', key: newSequenceNameKey });
        dispatch({
          type: 'ADD_METHOD_SEQUENCE',
          payload: response.data,
          projectTradeId,
        });
      })
      .catch(() =>
        addError(uuidv4(), { fullMessage: i18nTShared('errors.generic') }),
      );
  };

  const handleNewSequenceNameChange = (e) => {
    dispatch({
      type: 'EDITING_CONTENT_CHANGE',
      key: `projectTradeNewSequence--${projectTradeId}`,
      value: e.target.value,
    });
  };

  return (
    <div
      className={classNames(
        'tw-mb-6',
        isFocused && !isOrdering && 'tw-relative tw-z-[1]',
      )}
    >
      <RowBar
        actions={
          <AddButton
            customStyleClasses='mw-unset'
            disabled={false}
            onClick={() =>
              dispatch({
                type: 'UI_TOGGLE',
                value: true,
                key: newSequenceNameKey,
              })
            }
            text={i18nT('addTask')}
          />
        }
        additionalClasses='drag-handle-container tw-pl-0 tw-static'
        content={
          <>
            <DragHandle />
            <h3
              className={classNames(
                'tw-m-0 tw-py-4.5 tw-text-l tw-font-semibold',
                isOrdering && 'truncated-text-container',
              )}
            >
              {tradeName}
            </h3>
          </>
        }
        modifiers={modifiers}
      />

      {!isOrdering && (
        <div className='header-bar header-bar--border tw-border-grey-100 tw-bg-grey-050 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
          {i18nT('task')}
        </div>
      )}
      <div
        className={classNames(
          'collapsable-list',
          isOrdering ? 'tw-h-0 tw-overflow-hidden' : 'tw-h-auto',
        )}
      >
        <animated.div style={{ transform: springProps.transform }}>
          <div>
            <AvailableSequenceList
              containerRef={containerRef}
              projectTradeId={projectTradeId}
            />
            {isAddingSequence && (
              <div className='row-bar row-bar--border-top-none row-bar--additional-row tw-block tw-border-grey-100 tw-bg-white'>
                <SubmittableField
                  autoFocus={true}
                  onCancel={() =>
                    dispatch({
                      type: 'CLEAR_TOGGLE_CONTENT',
                      key: newSequenceNameKey,
                    })
                  }
                  onChange={handleNewSequenceNameChange}
                  onSubmit={handleSequenceSubmit}
                  placeholderText={i18nT('submittableField.placeholder')}
                  submitButtonText={i18nT('submittableField.submitButton')}
                  value={newSequenceText}
                />
              </div>
            )}
          </div>
        </animated.div>
      </div>
    </div>
  );
}
